import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  CompleteLocation,
  injectionKey,
  OSDExposition,
} from '@/core/components/data-display/OSD/osd.shared';
import OpenSeadragon from 'openseadragon';


export default /*#__PURE__*/_defineComponent({
  __name: 'NkOSDOverlayCreator',
  props: {
  injectionKey: {
    type: Symbol,
    default: injectionKey,
  },
},
  emits: ["create"],
  setup(__props, { emit }: { emit: ({
  (event: 'create', completeLocation: CompleteLocation | null): void
}), expose: any, slots: any, attrs: any }) {

const props = __props



const injected = inject<OSDExposition>(props.injectionKey);

const el = ref<HTMLElement>();
const eventsToDisable: (keyof OpenSeadragon.ViewerEventMap)[] = ['canvas-click', 'canvas-double-click', 'zoom', 'pan', 'rotate', 'canvas-scroll', 'canvas-drag', 'canvas-pinch'];

const mousePosition = ref<OpenSeadragon.Point>(new OpenSeadragon.Point());
const positions = ref<[OpenSeadragon.Point | null, OpenSeadragon.Point | null]>([null, null]);

const elPositions = computed(() => {
  const minX = Math.min(positions.value[0]?.x ?? 0, mousePosition.value.x ?? 0);
  const minY = Math.min(positions.value[0]?.y ?? 0, mousePosition.value.y ?? 0);
  const maxX = Math.max(positions.value[0]?.x ?? 0, mousePosition.value.x ?? 0);
  const maxY = Math.max(positions.value[0]?.y ?? 0, mousePosition.value.y ?? 0);
  const width = maxX - minX;
  const height = maxY - minY;
  return {
    minX,
    minY,
    maxX,
    maxY,
    width,
    height,
  };
});

const elStyle = computed(() => {
  const {
    minX,
    minY,
    width,
    height,
  } = elPositions.value;
  return {
    top: `${ minY }px`,
    left: `${ minX }px`,
    width: `${ width }px`,
    height: `${ height }px`,
  };
});

let mouseTracker: OpenSeadragon.MouseTracker;

const disableEvent = (event: OpenSeadragon.ViewerEventMap[keyof OpenSeadragon.ViewerEventMap]) => {
  // eslint-disable-next-line no-param-reassign
  if ('preventDefaultAction' in event) event.preventDefaultAction = true;
};
const getViewer = () => {
  if (injected) {
    return injected.getViewer();
  }
  return null;
};
const getLocation = () => {
  const viewer = getViewer();
  if (viewer) {
    const viewportBounds = viewer.viewport.getBounds();
    const {
      minX,
      minY,
      width,
      height,
    } = elPositions.value;
    const positionCoordinates = viewer.viewport.deltaPointsFromPixels(new OpenSeadragon.Point(minX, minY));
    const sizeCoordinates = viewer.viewport.deltaPointsFromPixels(new OpenSeadragon.Point(width, height));

    const location = new OpenSeadragon.Rect(
      positionCoordinates.x + viewportBounds.x,
      positionCoordinates.y + viewportBounds.y,
      sizeCoordinates.x,
      sizeCoordinates.y,
    );

    const pixelLocation = viewer.viewport.viewportToImageRectangle(location);
    return {
      location,
      pixelLocation,
      viewportLocation: location,
    };
  }
  return null;
};

const onViewerCanvasPress = (event: OpenSeadragon.ViewerEventMap['canvas-press']) => {
  positions.value[0] = event.position;
};

const onViewerCanvasRelease = (event: OpenSeadragon.ViewerEventMap['canvas-release']) => {
  positions.value[1] = event.position;

  if (positions.value[0] && positions.value[1]) {
    emit('create', getLocation());
  }

  positions.value = [null, null];
};
// const onViewerCanvasClick = (event: OpenSeadragon.ViewerEventMap['canvas-click']) => {
//   if (!positions.value[0]) {
//     positions.value[0] = event.position;
//   } else if (!positions.value[1]) positions.value[1] = event.position;
//   if (positions.value[0] && positions.value[1]) {
//     emit('create', getLocation());
//     positions.value = [null, null];
//   }
// };

const onViewerCanvasMouseMove: OpenSeadragon.MouseTracker['moveHandler'] = (event) => {
  if ('position' in event) mousePosition.value = (event as unknown as { position: OpenSeadragon.Point }).position;
};

const addEventListeners = () => {
  const viewer = getViewer();
  if (viewer) {
    eventsToDisable.forEach(event => {
      viewer.addHandler(event, disableEvent);
    });
    // viewer.addHandler('canvas-click', onViewerCanvasClick);
    viewer.addHandler('canvas-press', onViewerCanvasPress);
    viewer.addHandler('canvas-release', onViewerCanvasRelease);
    mouseTracker = new OpenSeadragon.MouseTracker({
      element: viewer.canvas,
      moveHandler: onViewerCanvasMouseMove,
    });
  }
};

const removeEventListeners = () => {
  const viewer = getViewer();
  if (viewer) {
    eventsToDisable.forEach(event => {
      viewer.removeHandler(event, disableEvent);
    });
    // viewer.removeHandler('canvas-click', onViewerCanvasClick);
    viewer.removeHandler('canvas-press', onViewerCanvasPress);
    viewer.removeHandler('canvas-release', onViewerCanvasRelease);
    if (mouseTracker) mouseTracker.destroy();
  }
};

onMounted(() => {
  if (injected) {
    injected.isReady()
      .then(() => {
        addEventListeners();
      });
  }
});

onBeforeUnmount(async () => {
  removeEventListeners();
});

return (_ctx: any,_cache: any) => {
  return (positions.value[0])
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "el",
        ref: el,
        style: _normalizeStyle(_unref(elStyle)),
        class: "absolute h-full w-full pointer-events-none"
      }, [
        _renderSlot(_ctx.$slots, "default")
      ], 4))
    : _createCommentVNode("", true)
}
}

})